import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api } from '../api/client'
import type { Alarm, Sensor, SensorDetail, SensorFile, SensorProperties, Station } from '../api/types'
import { SENSOR_TYPES } from '../api/types'
import { setChannelHidden, showAllChannels, useHiddenChannels } from '../lib/hiddenChannels'
import { ALERTS_REFRESH_MS, LiveStationAlerts } from '../components/LiveStationAlerts'
import { useAsync } from '../lib/useAsync'
import { usePolled } from '../lib/usePolled'
import { checksStale } from '../lib/health'
import { formatMetres } from '../lib/geo'
import { alarmPill, NO_TYPE, STATUS_WORD, readingsHref } from '../lib/sensorMap'
import {
  canPlaceBySurvey, clearsPair, crsChoices, crsLabel, draftErrors, draftFrom, formatDay, METRES,
  pairWarning, parseMetres, patchFrom, positionNote, shownSurvey, surveyNote, withField, type SensorDraft,
} from '../lib/sensorForm'
import { formatDateTime } from '../lib/time'
import { StationMetadata } from '../components/StationHeader'
import { useMovable } from '../lib/useMovable'
import { BADGE, documentRefusal } from '../lib/projectFiles'
import { csvValue } from '../lib/compare'
import { manualSensorStore, readingTimeLabel } from '../lib/manualSensors'
import { useRemote } from '../lib/remoteStore'
import './sensor-info.css'

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

/** Sensor info (redesign phase 6, the StationSensors board): a station's
 *  sensors, each one's properties, where it is, notes, images and PDFs. A
 *  sensor is a gateway node, or an MDS or MRL station's logger. Everyone
 *  reads; the pencils show only to a platform admin or a project Editor or
 *  Admin on the station (the api decides, `can_edit`).
 *
 *  The owner's review of 2026-10-22 shaped what is here: the position is its
 *  UTM survey only, the station's archive and transmission facts are rows
 *  of Properties rather than a fold, and its latest observations are not
 *  shown (they are the readings page's, one Chart click away). */
export function SensorInfo() {
  const { code, sensor: key } = useParams()
  const stations = useAsync(() => api.stations(), [])
  const station = (stations.data ?? []).find((s) => s.code === code)
  // Every sensor the reader can see, for the drill-down list (Tamar,
  // 2026-10-28): the right side stays empty until one is picked.
  const listed = usePolled(() => api.sensors.list(), [], 60_000)
  const sensors = listed.data?.sensors ?? []

  const alarms = useAsync(() => (code ? api.alerts.alarms({ station: code }) : Promise.resolve(null)),
                          [code])
  const connection = usePolled(() => code ? api.stationAlerts(code) : Promise.resolve(undefined),
                               [code], ALERTS_REFRESH_MS)
  const stale = checksStale(connection.data ? connection.data.health_checked_at
    : listed.data ? listed.data.health_checked_at : station?.health_checked_at)

  if (stations.error) return <p className="pad error">{stations.error}</p>
  if (!stations.data) return <p className="pad muted">Loading…</p>

  return (
    <div className="sensor-info">
      <div className="sensor-info-body">
        {/* A bare station address (Explore's "View station status", the
            Overview's quiet-channel links) opens on that station's sensors. */}
        <SensorList sensors={sensors} current={code && key ? `${code}/${key}` : undefined} stale={stale}
                    initialStation={code && !key ? station?.name : undefined}
                    loading={listed.loading && !listed.data} error={listed.error} />
        <div className="sensor-main">
          {code && key && station ? <>
            {/* The station's notices above its sensor: a stopped health
                check must not read as all-clear. Hidden when there is
                nothing to say. */}
            <div className="sensor-info-alerts">
              <LiveStationAlerts name={station.name} data={connection.data} />
              {connection.error && <p className="error">{connection.error}</p>}
            </div>
            <SensorPanel key={`${code}/${key}`} code={code} sensorKey={key} stale={stale}
                         station={station} alarms={alarms.data?.alarms ?? []}
                         onSaved={() => listed.reload()} />
          </> : code && !station ? <p className="muted">No station with code {code}.</p>
            : code && station ? <>
              {/* The station asked for, with no sensor chosen yet: its
                  notices come first, so a stopped health check or a quiet
                  channel is the first thing read. */}
              <div className="sensor-info-alerts">
                <LiveStationAlerts name={station.name} data={connection.data} />
                {connection.error && <p className="error">{connection.error}</p>}
              </div>
              <div className="sensor-empty"><p>Choose a sensor of {station.name} on the left.</p>
                <p className="muted small">The list is narrowed to this station. Clear the station box to see every sensor.</p></div>
            </>
            : <div className="sensor-empty"><p>Choose a sensor on the left.</p>
              <p className="muted small">Narrow the list by project, then station, then sensor. Part of a name is enough.</p></div>}
        </div>
      </div>
    </div>
  )
}

/** The drill-down list (Tamar, 2026-10-28, as Explore data's): a project,
 *  then a station, then a sensor, each matched anywhere in the name, and
 *  the sensors listed under their stations. */
function SensorList({ sensors, current, stale, loading, error, initialStation }: {
  sensors: Sensor[]; current: string | undefined; stale: boolean
  loading: boolean; error: string | undefined
  /** The station box starts with this name (a bare station address). */
  initialStation?: string
}) {
  const [open, setOpen] = useState(true)
  const [drill, setDrill] = useState({ project: '', station: initialStation ?? '', sensor: '' })
  const [type, setType] = useState('')
  const types = useMemo(() => {
    const set = new Set(sensors.map((s) => s.sensor_type ?? NO_TYPE))
    const named = [...set].filter((t) => t !== NO_TYPE).sort()
    return set.has(NO_TYPE) ? [...named, NO_TYPE] : named
  }, [sensors])
  const has = (text: string, q: string) => !q.trim() || text.toLocaleLowerCase().includes(q.trim().toLocaleLowerCase())
  const shown = sensors.filter((s) => (!type || (s.sensor_type ?? NO_TYPE) === type)
    && has(s.station.projects.join(' '), drill.project) && has(`${s.station.name} ${s.station.code}`, drill.station)
    && has(`${s.name} ${s.model ?? ''} ${s.key} ${s.sensor_type ?? ''}`, drill.sensor))
  const byStation = new Map<string, Sensor[]>()
  for (const s of shown) byStation.set(s.station.code, [...(byStation.get(s.station.code) ?? []), s])
  const groups = [...byStation.values()].sort((a, b) => a[0]!.station.name.localeCompare(b[0]!.station.name))
  // Gauges read by hand (db/034), in the reader's projects: listed beside
  // the collected sensors, each opening its own page, where its readings
  // are entered. They have no status: nothing collects them.
  const manual = useRemote(manualSensorStore).items.filter((m) => has(m.project ?? '', drill.project)
    && has(`${m.station_name ?? ''} ${m.station ?? ''}`, drill.station) && has(`${m.name} ${m.quantity}`, drill.sensor))

  if (!open) {
    return (
      <aside className="sensor-list collapsed" aria-label="Sensors">
        <button type="button" className="icon-button" aria-label="Show the sensor list"
                title="Show the sensor list" aria-expanded="false" onClick={() => setOpen(true)}>
          <Hamburger />
        </button>
      </aside>
    )
  }
  return (
    <aside className="sensor-list" aria-label="Sensors">
      <div className="sensor-list-head">
        <div className="sensor-list-row">
          <button type="button" className="icon-button" aria-label="Hide the sensor list"
                  title="Hide the sensor list" aria-expanded="true" onClick={() => setOpen(false)}>
            <Hamburger />
          </button>
          <h2>Sensors</h2>
          <span className="muted small">{shown.length} of {sensors.length}</span>
        </div>
        <div className="explore-drill" role="group" aria-label="Find sensors">
          {(['project', 'station', 'sensor'] as const).map((k) => (
            <label key={k} className="explore-drill-field">
              <span>{{ project: 'Project', station: 'Station', sensor: 'Sensor' }[k]}</span>
              <input type="search" value={drill[k]} placeholder={{ project: 'Any project', station: 'Any station', sensor: 'Name or model' }[k]}
                     onChange={(e) => setDrill((was) => ({ ...was, [k]: e.target.value }))} />
            </label>
          ))}
        </div>
        <label className="map-field">Sensor type
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <div className="row-buttons">
          <button type="button" className="button small" onClick={() => { setDrill({ project: '', station: '', sensor: '' }); setType('') }}>Clear</button>
        </div>
      </div>
      <div className="sensor-list-items">
        {loading && <p className="muted small">Loading…</p>}
        {error && <p className="error">{error}</p>}
        {!loading && shown.length === 0 && <p className="muted small">No sensor matches the filters.</p>}
        {groups.map((group) => (
          <div key={group[0]!.station.code} className="sensor-list-station">
            <h3 className="sensor-list-sub">{group[0]!.station.name}</h3>
            {group.map((s) => {
              const status = stale ? 'not_checked' : s.status
              const id = `${s.station.code}/${s.key}`
              return (
                <Link key={id} className="place-row" aria-current={id === current ? 'page' : undefined}
                      to={`/sensors/info/${s.station.code}/${encodeURIComponent(s.key)}`}>
                  <span className={`dot ${status}`} aria-hidden="true" />
                  <span className="place-text">
                    <span className="place-name">{s.name}</span>
                    <span className="place-sub">{s.sensor_type ?? NO_TYPE}</span>
                  </span>
                  <span className="visually-hidden">{STATUS_WORD[status]}</span>
                </Link>
              )
            })}
          </div>
        ))}
        {manual.length > 0 && <>
          <h3 className="sensor-list-sub">Manual sensors</h3>
          {manual.map((m) => (
            <Link key={m.id} className="place-row" to={`/sensors/manual/${encodeURIComponent(m.id)}`}>
              <span className="place-text">
                <span className="place-name">{m.name}</span>
                <span className="place-sub">{m.quantity}{m.latest
                  ? ` · ${csvValue(m.latest.value, m.precision)}${m.unit ? ' ' + m.unit : ''}, ${readingTimeLabel(m.latest.ts)}` : ' · no readings yet'}</span>
              </span>
            </Link>
          ))}
        </>}
      </div>
      <div className="status-legend">
        <span><span className="dot receiving" aria-hidden="true" /> Receiving</span>
        <span><span className="dot no_data" aria-hidden="true" /> No data</span>
      </div>
    </aside>
  )
}

function SensorPanel({ code, sensorKey, stale, station, alarms, onSaved }: {
  code: string; sensorKey: string; stale: boolean; station: Station | undefined; alarms: Alarm[]
  onSaved: () => void
}) {
  const [detail, setDetail] = useState<SensorDetail>()
  const [error, setError] = useState<string>()
  const [editing, setEditing] = useState(false)
  const reload = () => api.sensors.get(code, sensorKey).then(
    (d) => { setDetail(d); setError(undefined) },
    (e) => setError(errorText(e, 'Could not read the sensor.')))
  useEffect(() => { void reload() }, [code, sensorKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // A sensor that cannot be read (a stale link, a removed sensor) has no
  // Properties card, but the station's archive and transmission facts still
  // belong on the page (review finding 4).
  if (error) return <><p className="error">{error}</p>{station && <StationCard station={station} />}</>
  if (!detail) return <p className="muted">Loading…</p>
  const { sensor, properties, can_edit: canEdit } = detail
  const status = stale ? 'not_checked' : sensor.status
  const tone = status === 'receiving' ? 'normal' : status === 'no_data' ? 'alarm' : 'stale'
  const pill = alarmPill(alarms, code)
  const saved = (next: SensorDetail) => { setDetail(next); onSaved() }

  return (
    <>
      <div className="sensor-title">
        <div className="sensor-title-name">
          <h2>{sensor.name}</h2>
          <span className={`pill status-${tone}`} title={statusNote(sensor.status_reason)}>
            {STATUS_WORD[status]}
          </span>
          <span className={`pill status-${pill.tone}`}>{pill.text}</span>
        </div>
        <div className="row-buttons">
          <Link className="button" to={readingsHref(code, sensor.instrument_id)}>Chart</Link>
          <Link className="button" to={`/map?layer=sensors&select=${encodeURIComponent(`sensor:${code}:${sensor.key}`)}`}>
            Show on map
          </Link>
        </div>
      </div>
      <div className="sensor-columns">
        <div className="sensor-column">
          <Properties detail={detail} station={station} onEdit={canEdit ? () => setEditing(true) : undefined} />
          <Channels code={code} instrument={sensor.instrument_id} />
        </div>
        <div className="sensor-column">
          <Notes code={code} sensorKey={sensorKey} notes={properties.notes} canEdit={canEdit} onSaved={saved} />
          <Files kind="image" files={detail.files} code={code} sensorKey={sensorKey} canEdit={canEdit}
                 onChanged={() => void reload().then(onSaved)} />
          <Files kind="document" files={detail.files} code={code} sensorKey={sensorKey} canEdit={canEdit}
                 onChanged={() => void reload().then(onSaved)} />
        </div>
      </div>
      {editing && (
        <EditProperties detail={detail} code={code} sensorKey={sensorKey}
                        onClose={() => setEditing(false)} onSaved={(next) => { saved(next); setEditing(false) }} />
      )}
    </>
  )
}

/** The channels this sensor transmits (Tamar, 2026-10-28), link telemetry
 *  aside, each with a switch that leaves it out of the first chart on this
 *  reader's reading pages: display only, nothing stops being listed,
 *  collected, stored or exported. The
 *  choice is one of the reader's workspace documents (lib/hiddenChannels), so
 *  it follows them to any browser; it cannot be changed while the documents
 *  have not loaded, and the card says so. */
function Channels({ code, instrument }: { code: string; instrument: number | null }) {
  const catalogue = useAsync(() => api.compareChannels(), [])
  const hidden = useHiddenChannels()
  const [refused, setRefused] = useState(false)
  const channels = (catalogue.data ?? []).filter((c) => c.station === code && c.instrument_id === instrument)
  const hiddenHere = channels.filter((c) => hidden.has(`${c.station}|${c.parameter}`)).length
  const attempt = (saved: boolean) => setRefused(!saved)
  return (
    <section className="sensor-card sensor-channels" aria-labelledby="sensor-channels-title">
      <header>
        <h3 id="sensor-channels-title">Channels <span className="muted small">{channels.length}</span></h3>
        {hiddenHere > 0 && <button type="button" className="link" onClick={() => attempt(showAllChannels(code))}>Show all</button>}
      </header>
      {catalogue.loading && <p className="muted small">Loading…</p>}
      {catalogue.error && <p className="error">{catalogue.error}</p>}
      {catalogue.data && channels.length === 0 && <p className="muted small">No channels with readings yet.</p>}
      {refused && <p className="error" role="alert">Your settings have not loaded, so this was not saved. Reload the page and try again.</p>}
      {channels.length > 0 && <>
        <p className="muted small">Untick a channel to leave it out of the first chart on the reading pages, for you only, on any browser you sign in with. It is still listed there, collected, stored and exported.</p>
        <ul className="sensor-channel-list">
          {channels.map((c) => {
            const id = `${c.station}|${c.parameter}`
            return <li key={id}><label>
              <input type="checkbox" checked={!hidden.has(id)} onChange={(e) => attempt(setChannelHidden(id, !e.target.checked))} />
              <span className="sensor-channel-name">{c.display_name}</span>
              <span className="muted small">{c.unit || 'unitless'}</span>
            </label></li>
          })}
        </ul>
      </>}
    </section>
  )
}

/** The station's facts on their own, where no sensor's Properties card is
 *  there to hold them: a station with no sensors, or a sensor that could
 *  not be read. */
function StationCard({ station }: { station: Station }) {
  return (
    <section className="sensor-card sensor-props" aria-labelledby="station-props-title">
      <header><h3 id="station-props-title">Station and Transmission</h3></header>
      <StationMetadata station={station} />
    </section>
  )
}

function statusNote(reason: string | null): string | undefined {
  return {
    sensor_silent: 'No reading from this sensor for longer than its station is allowed to be silent.',
    sensor_never_read: 'No reading from this sensor is stored.',
    silent: 'Its station has gone silent.',
    collection_off: 'Collection is off for its station.',
    decommissioned: 'Its station is decommissioned.',
    never_read: 'Its station has never been read.',
  }[reason ?? ''] ?? undefined
}

const NOT_SET = 'Not set'

interface PropertyRow { label: string; value: string; mono?: boolean }

function Properties({ detail, station, onEdit }: {
  detail: SensorDetail; station: Station | undefined; onEdit?: () => void
}) {
  const p = detail.properties
  // Latitude, longitude and height are left out on the owner's word
  // (review of 2026-10-22): the position reads as the UTM survey alone, a
  // stored latitude and longitude converted when no survey is stored.
  const shown = shownSurvey(p)
  const metres = (v: number | null) => (v === null ? NOT_SET : formatMetres(v))
  const rows: PropertyRow[] = [
    { label: 'Name', value: detail.sensor.name },
    ...(detail.sensor.model ? [{ label: 'Model', value: detail.sensor.model }] : []),
    { label: 'Type', value: p.sensor_type ?? NOT_SET },
    { label: 'Northing', value: metres(shown.northing), mono: true },
    { label: 'Easting', value: metres(shown.easting), mono: true },
    { label: 'Elevation', value: metres(p.elevation_m), mono: true },
    { label: 'Coordinate system', value: shown.epsg === null ? NOT_SET : crsLabel(shown.epsg) },
    { label: 'Active', value: p.active === null ? NOT_SET : p.active ? 'Yes' : 'No' },
    p.reference_mode === 'day' && p.reference_day
      ? { label: 'Effective reference date', value: formatDay(p.reference_day), mono: true }
      : { label: 'Effective reference date',
          value: p.reference_mode === 'oldest' ? 'Oldest available observation' : NOT_SET },
  ]
  return (
    <section className="sensor-card sensor-props" aria-labelledby="props-title">
      <header>
        <h3 id="props-title">Properties</h3>
        {onEdit && (
          <button type="button" className="icon-button small" aria-label="Edit properties"
                  title="Edit properties" onClick={onEdit}><Pencil /></button>
        )}
      </header>
      <dl className="props">
        {rows.map(({ label, value, mono }) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className={value === NOT_SET ? 'muted' : mono ? 'mono' : undefined}>{value}</dd>
          </div>
        ))}
      </dl>
      <p className="muted small sensor-card-foot">
        {positionNote(shown, detail.sensor.position)}
        {detail.updated_by && detail.updated_at && (
          <> Last changed by {detail.updated_by}, <span className="mono">{formatDateTime(detail.updated_at)}</span>.</>
        )}
      </p>
      {station && (
        <div className="sensor-props-group" role="group" aria-labelledby="station-props-title">
          <h4 id="station-props-title">Station and Transmission</h4>
          <StationMetadata station={station} />
        </div>
      )}
    </section>
  )
}

function Notes({ code, sensorKey, notes, canEdit, onSaved }: {
  code: string; sensorKey: string; notes: string; canEdit: boolean; onSaved: (d: SensorDetail) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const save = async () => {
    setSaving(true)
    setError(undefined)
    try {
      onSaved(await api.sensors.update(code, sensorKey, { notes: draft ?? '' }))
      setDraft(null)
    } catch (e) {
      setError(errorText(e, 'Could not save the notes.'))
    } finally {
      setSaving(false)
    }
  }
  return (
    <section className="sensor-card padded" aria-labelledby="notes-title">
      <header className="bare">
        <h3 id="notes-title">Notes</h3>
        {canEdit && draft === null && (
          <button type="button" className="icon-button small" aria-label="Edit notes" title="Edit notes"
                  onClick={() => setDraft(notes)}><Pencil /></button>
        )}
      </header>
      {draft === null
        ? <p className={notes ? 'notes' : 'muted'}>{notes || 'No notes yet.'}</p>
        : (
          <>
            <textarea aria-label="Notes" rows={4} maxLength={4000} value={draft} autoFocus
                      onChange={(e) => setDraft(e.target.value)} />
            {error && <p className="error">{error}</p>}
            <div className="row-buttons end">
              <button type="button" className="button" onClick={() => setDraft(null)}>Cancel</button>
              <button type="button" className="button primary" disabled={saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
    </section>
  )
}

function Files({ kind, files, code, sensorKey, canEdit, onChanged }: {
  kind: 'image' | 'document'; files: SensorFile[]; code: string; sensorKey: string; canEdit: boolean
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState<string>()
  const [messages, setMessages] = useState<string[]>([])
  const [confirming, setConfirming] = useState<number | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const own = files.filter((f) => f.kind === kind)
  const title = kind === 'image' ? 'Images' : 'Documents'
  const upload = async (list: FileList | null) => {
    if (!list?.length) return
    const notes: string[] = []
    for (const file of Array.from(list)) {
      const refusal = kind === 'document' ? documentRefusal(file) : null
      if (refusal) { notes.push(refusal); continue }
      setBusy(`${file.name}: 0%`)
      try {
        const result = await api.sensors.upload(code, sensorKey, kind, file,
          (f) => setBusy(`${file.name}: ${Math.round(f * 100)}%`))
        result.refused.forEach((r) => notes.push(r.reason))
      } catch (e) {
        notes.push(errorText(e, `${file.name} could not be uploaded.`))
      }
    }
    setBusy(undefined)
    setMessages(notes)
    if (input.current) input.current.value = ''
    onChanged()
  }
  const remove = async (file: SensorFile) => {
    try {
      await api.sensors.removeFile(file.id)
      setConfirming(null)
      onChanged()
    } catch (e) {
      setMessages([errorText(e, `${file.name} could not be removed.`)])
    }
  }
  return (
    <section className="sensor-card padded" aria-labelledby={`${kind}-title`}>
      <header className="bare">
        <h3 id={`${kind}-title`}>{title}</h3>
        {canEdit && !editing && (
          <button type="button" className="icon-button small" aria-label={`Add or remove ${title.toLowerCase()}`}
                  title={`Add or remove ${title.toLowerCase()}`} onClick={() => setEditing(true)}><Pencil /></button>
        )}
        {canEdit && editing && (
          <div className="row-buttons">
            <button type="button" className="button small" disabled={busy !== undefined}
                    onClick={() => input.current?.click()}>
              {kind === 'image' ? 'Add image' : 'Add document'}
            </button>
            <button type="button" className="button small primary" onClick={() => setEditing(false)}>Done</button>
          </div>
        )}
      </header>
      <input ref={input} type="file" hidden multiple
             accept={kind === 'image' ? 'image/*,.heic,.heif' : undefined}
             onChange={(e) => void upload(e.target.files)} />
      {busy && <p className="muted small" role="status">Uploading {busy}</p>}
      {messages.map((m) => <p key={m} className="error small">{m}</p>)}
      {own.length === 0
        ? <p className="muted">{kind === 'image' ? 'No images yet.' : 'No documents yet.'}</p>
        : (
          <ul className={kind === 'image' ? 'sf-tiles' : 'sf-tiles docs'}>
            {own.map((f) => (
              <li key={f.id}>
                {/* A real PDF or an image opens in a new tab; any other document downloads. */}
                <a href={kind === 'image' ? f.urls.preview : f.urls.original}
                   {...(kind === 'image' || f.inline ? { target: '_blank', rel: 'noreferrer' } : { download: f.name })}
                   title={`${f.name}, ${f.uploaded_by ?? 'someone'}, ${formatDateTime(f.created_at)}`}>
                  {kind === 'image'
                    ? <img src={f.urls.thumb} alt={f.name} loading="lazy" />
                    : <span className="doc-badge">{BADGE[f.family]}</span>}
                  <span className="tile-name">{f.name}</span>
                </a>
                {editing && confirming !== f.id && (
                  <button type="button" className="tile-remove" aria-label={`Remove ${f.name}`}
                          onClick={() => setConfirming(f.id)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" fill="none" />
                    </svg>
                  </button>
                )}
                {editing && confirming === f.id && (
                  <span className="tile-confirm" role="group" aria-label={`Remove ${f.name}?`}>
                    {f.shared_with > 0 && (
                      <span className="muted small">
                        Also on {f.shared_with} other sensor{f.shared_with === 1 ? '' : 's'}; they keep it.
                      </span>
                    )}
                    <button type="button" className="link danger" onClick={() => void remove(f)}>Remove</button>
                    <button type="button" className="link" onClick={() => setConfirming(null)}>Cancel</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
    </section>
  )
}

function EditProperties({ detail, code, sensorKey, onClose, onSaved }: {
  detail: SensorDetail; code: string; sensorKey: string; onClose: () => void
  onSaved: (d: SensorDetail) => void
}) {
  const [draft, setDraft] = useState<SensorDraft>(() => draftFrom(detail.properties))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const movable = useMovable()
  const first = useRef<HTMLSelectElement>(null)
  // The parent re-renders on every poll and hands a new onClose each time;
  // the effect below must not re-run then, or it would pull focus back to
  // Type while someone types a coordinate, and a keystroke landing on the
  // select would change the stored type (review finding 3).
  const close = useRef(onClose)
  close.current = onClose
  const errors = draftErrors(draft, detail.properties)
  const note = surveyNote(detail.properties)
  const far = pairWarning(draft, detail.properties)
  // A changed whole survey clears the pair anyway, so the box shows ticked
  // and cannot be unticked then; it only matters for an unchanged one.
  const surveyClears = clearsPair({ ...draft, place_by_survey: false }, detail.properties)
  const set = <K extends keyof SensorDraft>(k: K, v: SensorDraft[K]) => setDraft((d) => withField(d, k, v))
  // Field messages are tied to their input (aria-describedby) and read when
  // it is focused. Only a number that cannot be read is announced as typed:
  // a survey still being filled in would otherwise announce what is missing
  // at the first digit (review finding 5).
  const fieldError = (field: keyof SensorDraft, live: boolean) => errors[field] && (
    <span id={`edit-props-${field}-error`} className="field-error" role={live ? 'alert' : undefined}>
      {errors[field]}
    </span>
  )
  const describedBy = (field: keyof SensorDraft) => (errors[field] ? `edit-props-${field}-error` : undefined)

  useEffect(() => {
    first.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const save = async () => {
    const patch = patchFrom(draft, detail.properties as SensorProperties)
    if (Object.keys(patch).length === 0) { onClose(); return }
    setSaving(true)
    setError(undefined)
    try {
      onSaved(await api.sensors.update(code, sensorKey, patch))
    } catch (e) {
      setError(errorText(e, 'Could not save the properties.'))
    } finally {
      setSaving(false)
    }
  }
  const metreLabel = { northing_m: 'Northing, m', easting_m: 'Easting, m', elevation_m: 'Elevation, m' }
  return (
    <div className="dialog-backdrop">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="edit-props-title" style={movable.style}>
        <div className="dialog-head">
          <button type="button" className="icon-button small grip" aria-label="Move the window" {...movable.grip}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              {[6, 12, 18].flatMap((y) => [9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
            </svg>
          </button>
          <div className="dialog-title" {...movable.handle}>
            <h2 id="edit-props-title">Edit properties</h2>
            <p className="muted small">{detail.sensor.name}, {detail.sensor.station.name}</p>
          </div>
          <button type="button" className="icon-button plain" aria-label="Close without saving" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
        </div>
        <div className="dialog-body">
          <label className="map-field">Type
            <select ref={first} value={draft.sensor_type}
                    onChange={(e) => set('sensor_type', e.target.value as SensorDraft['sensor_type'])}>
              <option value="">Not set</option>
              {SENSOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="map-field">Active
            <select value={draft.active} onChange={(e) => set('active', e.target.value as SensorDraft['active'])}>
              <option value="">Not set</option><option value="yes">Yes</option><option value="no">No</option>
            </select>
          </label>
          {METRES.map((field) => (
            <label key={field} className="map-field">{metreLabel[field]}
              <input value={draft[field]} placeholder="0.000" inputMode="decimal"
                     aria-invalid={errors[field] ? true : undefined} aria-describedby={describedBy(field)}
                     onChange={(e) => set(field, e.target.value)} />
              {fieldError(field, parseMetres(draft[field]) === 'invalid')}
            </label>
          ))}
          <label className="map-field wide">Coordinate system of the northing and easting
            <select value={draft.crs_epsg} aria-invalid={errors.crs_epsg ? true : undefined}
                    aria-describedby={describedBy('crs_epsg')}
                    onChange={(e) => set('crs_epsg', e.target.value as SensorDraft['crs_epsg'])}>
              {crsChoices(detail.properties.crs_epsg).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {fieldError('crs_epsg', false)}
          </label>
          {/* The pair is no longer on the page, so say here what a save
              does to it (patchFrom clears it with a changed survey). */}
          {note && <p className="muted small sensor-dialog-note">{note}</p>}
          {canPlaceBySurvey(detail.properties) && (
            <label className="check sensor-dialog-check">
              <input type="checkbox" checked={surveyClears || draft.place_by_survey} disabled={surveyClears}
                     onChange={(e) => set('place_by_survey', e.target.checked)} />
              <span>Place on the map by this survey, clearing the stored latitude and longitude</span>
            </label>
          )}
          {/* Clearing the pair loses the only record of where the sensor was,
              so a survey that lands far from it is saved only on purpose. */}
          {far && (
            <div className="sensor-far">
              <p id="edit-props-far">{far}</p>
              <label className="check">
                <input type="checkbox" checked={draft.confirm_far} aria-describedby="edit-props-far"
                       onChange={(e) => set('confirm_far', e.target.checked)} />
                <span>Save it there all the same</span>
              </label>
            </div>
          )}
          <label className="map-field">Effective reference date
            <select value={draft.reference_mode}
                    onChange={(e) => set('reference_mode', e.target.value as SensorDraft['reference_mode'])}>
              <option value="">Not set</option>
              <option value="oldest">Oldest available observation</option>
              <option value="day">A chosen day</option>
            </select>
          </label>
          {draft.reference_mode === 'day' && (
            <label className="map-field">Reference day
              <input type="date" value={draft.reference_day} aria-invalid={errors.reference_day ? true : undefined}
                     aria-describedby={describedBy('reference_day')}
                     onChange={(e) => set('reference_day', e.target.value)} />
              {fieldError('reference_day', false)}
            </label>
          )}
        </div>
        {error && <p className="error dialog-error" role="alert">{error}</p>}
        <div className="dialog-foot">
          <p className="muted small">Its readings and history are untouched.</p>
          <button type="button" className="button" onClick={onClose}>Cancel</button>
          <button type="button" className="button primary" disabled={saving || Object.keys(errors).length > 0}
                  onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

function Pencil() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Hamburger() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
